import {
  array,
  boolean,
  discriminatedUnion,
  email,
  literal,
  number,
  object,
  type output,
  string,
} from 'zod';
import { getPlainText } from '../../common/html-string/get-plain-text';
import { isValidPhoneNumber, isValidUrl } from '../../shared/functions';

export const MAX_PARTNER_ID_LENGTH = 50;
export const MAX_NOMINATION_ABSTRACT_LENGTH = 500;
export const MAX_WRITTEN_NOMINATION_LENGTH = 8000;

const optionalOwnerTextSchema = string().nullish();

export const countryValidationSchema = object({
  countryId: number(),
  countryCode: string(),
  displayName: string(),
  postalCodeRequired: boolean(),
});

export const ownerValidationSchema = object({
  prefix: optionalOwnerTextSchema,
  firstName: string().min(1, 'Enter the first name.'),
  lastName: string().min(1, 'Enter the last name.'),
  title: optionalOwnerTextSchema,
});

export const nominationOwnerEmailValidationSchema = object({
  emailAddress: string()
    .min(1, 'Enter the email address.')
    .pipe(email({ error: 'Enter a valid email address.' })),
});

export const additionalContactValidationSchema = object({
  prefix: string(),
  firstName: string().min(1, 'Enter the first name.'),
  lastName: string().min(1, 'Enter the last name.'),
  emailAddress: string()
    .min(1, { error: 'Enter the email address.', abort: true })
    .pipe(email({ error: 'Enter a valid email address.' })),
  contactNotificationTypeId: number().positive({ error: 'Select a notification option.' }),
});

export const organizationValidationSchema = object({
  mpnId: string()
    .min(1, { error: 'Enter the Partner ID.', abort: true })
    .max(MAX_PARTNER_ID_LENGTH, `Use ${MAX_PARTNER_ID_LENGTH} digits or fewer for the Partner ID.`)
    .regex(/^\d+$/, 'Use digits only for the Partner ID.'),
  companyName: string().min(1, 'Enter the company name.'),
  address1: string().min(1, 'Enter the address.'),
  address2: string(),
  city: string().min(1, 'Enter the city.'),
  stateProvince: string(),
  postalCode: string().min(1, 'Enter the postal code.'),
  country: countryValidationSchema.refine(country => country.countryId > 0, {
    error: 'Select the country.',
  }),
  phoneNumber: string().min(1, 'Enter the phone number.'),
  websiteUrl: string()
    .min(1, { error: 'Enter the website URL.', abort: true })
    .refine(isValidUrl, 'Enter a valid website URL.'),
  regionalCeoFullName: string().optional(),
});

const countrylessPhoneNumberValidationSchema = object({
  phoneNumber: string(),
}).refine(
  input => input.phoneNumber.length === 0 || isValidPhoneNumber(input.phoneNumber),
  {
    error: 'Enter a valid phone number.',
    path: ['phoneNumber'],
  },
);

export const jointNomineeValidationSchema = organizationValidationSchema.and(object({
  prefix: string(),
  firstName: string().min(1, 'Enter the first name.'),
  lastName: string().min(1, 'Enter the last name.'),
  emailAddress: string()
    .min(1, { error: 'Enter the email address.', abort: true })
    .pipe(email({ error: 'Enter a valid email address.' })),
})).and(countrylessPhoneNumberValidationSchema);

export const customerReferenceValidationSchema = object({
  prefix: string(),
  firstName: string().min(1, 'Enter the first name.'),
  lastName: string().min(1, 'Enter the last name.'),
  title: string(),
  emailAddress: string()
    .min(1, { error: 'Enter the email address.', abort: true })
    .pipe(email({ error: 'Enter a valid email address.' })),
  phoneNumber: string().min(1, 'Enter the phone number.'),
  companyName: string().min(1, 'Enter the company name.'),
  description: string().min(1, 'Enter the description.'),
}).and(countrylessPhoneNumberValidationSchema);

export const nominationWebsiteValidationSchema = object({
  websiteURL: string()
    .min(1, { error: 'Enter the website URL.', abort: true })
    .refine(isValidUrl, 'Enter a valid website URL.'),
});

export const nominationSubmissionContentValidationSchema = object({
  name: string().min(1, 'Enter the solution name.'),
  abstract: string()
    .min(1, { error: 'Enter the nomination abstract.', abort: true })
    .max(MAX_NOMINATION_ABSTRACT_LENGTH, `Use ${MAX_NOMINATION_ABSTRACT_LENGTH} characters or fewer for the nomination abstract.`),
  customerReferences: array(customerReferenceValidationSchema),
  websites: array(nominationWebsiteValidationSchema),
});

export const writtenNominationValidationSchema = object({
  requiresWrittenNomination: literal(true),
  writtenNominationContent: string()
    .refine(content => getPlainText(content).length > 0, {
      error: 'Enter the written nomination.',
    })
    .refine(content => getPlainText(content).length <= MAX_WRITTEN_NOMINATION_LENGTH, {
      error: `Use ${MAX_WRITTEN_NOMINATION_LENGTH} characters or fewer for the written nomination.`,
    }),
  hasExecutiveSummaryDocument: boolean(),
});

export const templateNominationValidationSchema = object({
  requiresWrittenNomination: literal(false),
  writtenNominationContent: string(),
  hasExecutiveSummaryDocument: literal(true, {
    error: 'Upload the completed template.',
  }),
});

export const nominationDocumentValidationSchema = discriminatedUnion('requiresWrittenNomination', [
  writtenNominationValidationSchema,
  templateNominationValidationSchema,
]);

export type AdditionalContactValidation = output<typeof additionalContactValidationSchema>;
export type CustomerReferenceValidation = output<typeof customerReferenceValidationSchema>;
export type JointNomineeValidation = output<typeof jointNomineeValidationSchema>;
export type NominationOwnerEmailValidation = output<typeof nominationOwnerEmailValidationSchema>;
export type NominationWebsiteValidation = output<typeof nominationWebsiteValidationSchema>;
